import Box from '@mui/material/Box';
import { useQueryClient } from '@tanstack/react-query';
import {
  BroadcastAction,
  getV1CompaniesCompanyIdJobsId,
  Job_Read,
  JobLoadActivity_Broadcast,
  Ticket_Read,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import NoDataAlert from '~components/Common/NoDataAlert';
import {
  approveAllWithTracking,
  trackLoadsApproved,
} from '~hooks/useAnalytics/trackApprovals';
import { Job } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import { Load } from '~hooks/useLoad/models';
import { Order } from '~hooks/useOrders/models';
import { Ticket } from '~hooks/useTickets/models';
import TicketDrawer from '~pages/Approvals/Files/components/TicketDrawer/TicketDrawer';
import { makeJobsQueryContextKey } from '~pages/Approvals/Files/utils';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import { LoadsJobFilters } from '~types/loadsFilters';
import { getApprovalStatus } from '~utils/jobs/job-utils';
import {
  subscribeWithMultipleHandlers,
  unsubscribeHandler,
} from '~utils/rtu/rtu-multi-handler';
import { jobLoadActivityDataIsLoadRead } from '~utils/rtu/rtu-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { LoadingSpinner, SmallLoadingButton } from '../ordersDispatchStyledComponents';
import LoadsTabLoadsTable from './LoadsTabLoadsTable/LoadsTabLoadsTable';
import LoadsTabQueryContext, {
  LoadsTabQueryContextProvider,
} from './LoadsTabQueryContext';
import useCrossOrderNavigation, { LOADLESS_ORDER } from './useCrossOrderNavigation';

type TicketDrawerState = {
  job: Job;
  load?: Load;
  loadNumber?: number;
  ticket?: Ticket;
};

interface LoadsTabContentProps {
  filters?: LoadsJobFilters;
  order: Order;
}

const LoadsTabContentProvider = memo(function LoadsTabContentProvider({
  filters,
  order,
}: LoadsTabContentProps) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  return (
    <LoadsTabQueryContextProvider companyId={companyId} filters={filters} order={order}>
      <LoadsTabContent />
    </LoadsTabQueryContextProvider>
  );
});

export default LoadsTabContentProvider;

function LoadsTabContent() {
  const { userStore } = useStores();
  const userPermissions = userStore.getPermissions();
  const currentCompanyId = getEffectiveUserCompanyId(userStore);
  const queryClient = useQueryClient();
  const instanceId = useId();

  const { approveLoad } = useJob();
  const [isApprovingAllLoads, setIsApprovingAllLoads] = useState(false);
  const [ticketDrawer, setTicketDrawer] = useState<TicketDrawerState>();

  const crossOrderNav = useCrossOrderNavigation({
    onNavigate: (job, load, loadNumber, ticket) =>
      setTicketDrawer({ job, load, loadNumber, ticket }),
  });

  const {
    addTicket,
    clearQueryData,
    companyId,
    isFetchingJobs,
    isFetchingLoads,
    isFetchingTickets,
    jobsData,
    loadsData,
    order,
    refreshLoads,
    refreshLoadsAndTickets,
    removeStoredLoadById,
    ticketsData,
    toggleLoadsBusyState,
    updateStoredJob,
    updateStoredLoad,
    updateStoredTicket,
  } = useContext(LoadsTabQueryContext);

  const approvalCompanyId = order.company?.id ?? currentCompanyId;

  const effectiveLoads = crossOrderNav.crossOrderState?.loads ?? loadsData;
  const effectiveTickets = crossOrderNav.crossOrderState?.tickets ?? ticketsData;
  const ticketsDataRef = useRef(ticketsData);
  const jobsDataRef = useRef(jobsData);

  useEffect(() => {
    ticketsDataRef.current = ticketsData;
  }, [ticketsData]);

  useEffect(() => {
    jobsDataRef.current = jobsData;
  }, [jobsData]);

  const isFetching = isFetchingLoads || isFetchingTickets || isFetchingJobs;
  const approvableLoads = useMemo(() => {
    return loadsData.filter((load) => {
      const { isApprovable } = getApprovalStatus(
        load,
        userPermissions,
        Boolean(order.editable),
      );

      return isApprovable;
    });
  }, [loadsData, userPermissions, order.editable]);

  const hasApprovableLoads = approvableLoads.length > 0;

  const refreshJobsForOrder = useCallback(
    (orderId: string) => {
      const jobsQueryKey = makeJobsQueryContextKey(orderId);
      void queryClient.invalidateQueries({ queryKey: [jobsQueryKey] });
    },
    [queryClient],
  );

  const handleApproveAllLoadsClick = useCallback(async () => {
    if (!approvableLoads.length) {
      return;
    }

    const approvableLoadsIds = approvableLoads.map((load) => load.id);

    try {
      toggleLoadsBusyState(approvableLoadsIds);
      setIsApprovingAllLoads(true);

      await approveAllWithTracking(
        approvableLoads,
        (load) => approveLoad(load.id, approvalCompanyId),
        trackLoadsApproved,
      );
      await refreshLoads();
      refreshJobsForOrder(order.id);
    } finally {
      setIsApprovingAllLoads(false);
      toggleLoadsBusyState(approvableLoadsIds);
    }
  }, [
    approvableLoads,
    approvalCompanyId,
    order.id,
    refreshLoads,
    refreshJobsForOrder,
    toggleLoadsBusyState,
  ]);

  const handleTableRowClick = useCallback(
    async (loadNumber: number, load: Load, ticket?: Ticket) => {
      const response = await getV1CompaniesCompanyIdJobsId({
        path: { 'company-id': companyId, id: load.jobId },
      });
      const job = Job.parse(response.data.data);

      crossOrderNav.resetCrossOrderState();
      setTicketDrawer({ load, loadNumber, ticket, job });
    },
    [companyId, crossOrderNav.resetCrossOrderState],
  );

  const navigateToLoad = useCallback(
    async (loadNumber: number, load: Load, ticket?: Ticket) => {
      const currentJob = ticketDrawer?.job;

      if (currentJob && load.jobId === currentJob.id) {
        setTicketDrawer({ load, loadNumber, ticket, job: currentJob });
        return;
      }

      const response = await getV1CompaniesCompanyIdJobsId({
        path: { 'company-id': companyId, id: load.jobId },
      });
      const job = Job.parse(response.data.data);

      setTicketDrawer({ load, loadNumber, ticket, job });
    },
    [companyId, ticketDrawer?.job],
  );

  const handleRefreshTicket = useCallback(
    (ticket: Ticket, isNewTicket?: boolean) => {
      if (isNewTicket) {
        addTicket(ticket);
      } else {
        updateStoredTicket(ticket);
      }

      setTicketDrawer((state) => {
        if (!state) {
          return undefined;
        }

        if (isNewTicket || state.ticket?.id === ticket.id) {
          return { ...state, ticket };
        }

        return state;
      });
    },
    [addTicket, updateStoredTicket],
  );

  const [previousLoad, nextLoad] = useMemo(() => {
    if (!ticketDrawer?.load) {
      return [null, null];
    }

    const idx = effectiveLoads.findIndex((l) => l.id === ticketDrawer.load?.id);
    if (idx === -1) return [null, null];

    return [
      idx > 0 ? effectiveLoads[idx - 1] : null,
      idx < effectiveLoads.length - 1 ? effectiveLoads[idx + 1] : null,
    ];
  }, [effectiveLoads, ticketDrawer?.load]);

  const openLoadId = ticketDrawer ? (ticketDrawer.load?.id ?? LOADLESS_ORDER) : undefined;

  const crossOrderNext = crossOrderNav.getCrossOrderNext(openLoadId, effectiveLoads);
  const crossOrderPrevious = crossOrderNav.getCrossOrderPrevious(
    openLoadId,
    effectiveLoads,
  );

  const findTicketByLoadId = useCallback(
    (loadId: string) => {
      return effectiveTickets.find((ticket) => ticket.loadId === loadId);
    },
    [effectiveTickets],
  );

  const handleTicketRTU = useCallback(
    ({ data }: { data: Ticket_Read }) => {
      const parsedTicket = Ticket.parse(data);
      const isNewTicket =
        ticketsDataRef.current.findIndex((ticket) => ticket.id === parsedTicket.id) ===
        -1;

      if (isNewTicket) {
        addTicket(parsedTicket);
      } else {
        updateStoredTicket(parsedTicket);
      }
    },
    [addTicket, updateStoredTicket],
  );

  useEffect(() => {
    const ticketsKey = `${instanceId}_tickets`;
    subscribeWithMultipleHandlers(
      realTimeChannels.TicketUpdateChannel,
      currentCompanyId,
      ticketsKey,
      handleTicketRTU,
    );

    return () => {
      unsubscribeHandler(
        realTimeChannels.TicketUpdateChannel,
        currentCompanyId,
        ticketsKey,
      );
    };
  }, [currentCompanyId, handleTicketRTU, instanceId]);

  const handleJobRTU = useCallback(
    ({ data }: { data: Job_Read }) => {
      const parsedJob = Job.parse(data);
      const jobExistsInContext = jobsDataRef.current.some(
        (job) => job.id === parsedJob.id,
      );

      if (jobExistsInContext) {
        updateStoredJob(parsedJob);
        void refreshLoadsAndTickets();
      }
    },
    [refreshLoadsAndTickets, updateStoredJob],
  );

  useEffect(() => {
    const jobsKey = `${instanceId}_jobs`;
    subscribeWithMultipleHandlers(
      realTimeChannels.CompanyJobUpdateChannel,
      currentCompanyId,
      jobsKey,
      handleJobRTU,
    );

    return () => {
      unsubscribeHandler(
        realTimeChannels.CompanyJobUpdateChannel,
        currentCompanyId,
        jobsKey,
      );
    };
  }, [currentCompanyId, handleJobRTU, instanceId]);

  const handleLoadActivityRTU = useCallback(
    (payload: JobLoadActivity_Broadcast) => {
      if (payload.action === BroadcastAction.DELETE) {
        removeStoredLoadById(payload.data.id);
        return;
      }

      if (payload.action === BroadcastAction.CREATE) {
        void refreshLoads();
        return;
      }

      if (jobLoadActivityDataIsLoadRead(payload.data)) {
        updateStoredLoad(Load.parse(payload.data));
      }
    },
    [refreshLoads, removeStoredLoadById, updateStoredLoad],
  );

  // Joined into one string so the subscription effect re-runs only when the set of job
  // ids actually changes, not on every jobsData refetch (which yields a new array identity).
  const jobIdsForLoadActivity = useMemo(() => {
    return Array.from(new Set(jobsData.map((job) => job.id)))
      .sort()
      .join(',');
  }, [jobsData]);

  useEffect(() => {
    if (!jobIdsForLoadActivity) {
      return;
    }

    const jobIds = jobIdsForLoadActivity.split(',');
    const loadActivityKey = `${instanceId}_load_activity`;

    jobIds.forEach((jobId) => {
      subscribeWithMultipleHandlers(
        realTimeChannels.JobLoadActivityChannel,
        currentCompanyId,
        loadActivityKey,
        handleLoadActivityRTU,
        { job_id: jobId },
      );
    });

    return () => {
      jobIds.forEach((jobId) => {
        unsubscribeHandler(
          realTimeChannels.JobLoadActivityChannel,
          currentCompanyId,
          loadActivityKey,
          { job_id: jobId },
        );
      });
    };
  }, [currentCompanyId, handleLoadActivityRTU, instanceId, jobIdsForLoadActivity]);

  // The ticket drawer holds a snapshot of the load taken when it was opened. Re-sync it
  // whenever the stored loads change (e.g. an RTU landed) so its approval CTAs stay accurate.
  useEffect(() => {
    setTicketDrawer((state) => {
      if (!state) {
        return state;
      }

      if (!state.load) {
        return state;
      }

      const updatedLoad = loadsData.find((load) => load.id === state.load?.id);
      if (!updatedLoad || updatedLoad === state.load) {
        return state;
      }

      return { ...state, load: updatedLoad };
    });
  }, [loadsData]);

  useEffect(() => {
    return () => {
      clearQueryData();
    };
  }, [clearQueryData]);

  if (isFetching) {
    return <LoadingSpinner isVisible sx={{ px: 1, py: 2 }} />;
  }

  if (loadsData.length === 0) {
    return <NoDataAlert message={t('approvals.files.no_loads_alert')} sx={{ m: 1 }} />;
  }

  return (
    <Box sx={{ p: 1 }}>
      <LoadsTabLoadsTable
        jobs={jobsData}
        loads={loadsData}
        onTableRowClick={handleTableRowClick}
        order={order}
        tickets={ticketsData}
      />

      {ticketDrawer && (
        <TicketDrawer
          emptyStateMessage={t('ticket_drawer_v2.empty_order_message')}
          isDrawerOpen
          job={ticketDrawer.job}
          load={ticketDrawer.load}
          loadNumber={ticketDrawer.loadNumber}
          nextUnavailableReason={t('ticket_drawer_v2.end_of_queue')}
          previousUnavailableReason={t('ticket_drawer_v2.start_of_queue')}
          onClose={() => {
            crossOrderNav.resetCrossOrderState();
            setTicketDrawer(undefined);
          }}
          onShowNextTicketClick={
            crossOrderNav.isFetching
              ? undefined
              : nextLoad
                ? () => {
                    const nextLoadTicket = findTicketByLoadId(nextLoad.id);
                    const nextLoadNumber =
                      effectiveLoads.findIndex((load) => {
                        return load.id === nextLoad.id;
                      }) + 1;

                    navigateToLoad(nextLoadNumber, nextLoad, nextLoadTicket);
                  }
                : (crossOrderNext ?? undefined)
          }
          onShowPreviousTicketClick={
            crossOrderNav.isFetching
              ? undefined
              : previousLoad
                ? () => {
                    const previousLoadTicket = findTicketByLoadId(previousLoad.id);
                    const previousLoadNumber =
                      effectiveLoads.findIndex((load) => {
                        return load.id === previousLoad.id;
                      }) + 1;

                    navigateToLoad(previousLoadNumber, previousLoad, previousLoadTicket);
                  }
                : (crossOrderPrevious ?? undefined)
          }
          onLoadUpdated={(load) => {
            if (crossOrderNav.crossOrderState) {
              void crossOrderNav.refreshCrossOrderLoads();
            } else {
              updateStoredLoad(load);
            }
          }}
          refreshLoads={() => {
            if (crossOrderNav.crossOrderState) {
              void crossOrderNav.refreshCrossOrderLoads();
              refreshJobsForOrder(crossOrderNav.crossOrderState.order.id);
            } else {
              void refreshLoads();
            }
            refreshJobsForOrder(order.id);
          }}
          refreshTicket={(ticket, isNewTicket) => {
            if (crossOrderNav.crossOrderState) {
              crossOrderNav.updateCrossOrderTicket(ticket, isNewTicket);

              setTicketDrawer((state) => {
                if (!state) {
                  return undefined;
                }

                if (isNewTicket || state.ticket?.id === ticket.id) {
                  return { ...state, ticket };
                }

                return state;
              });
            } else {
              handleRefreshTicket(ticket, isNewTicket);
            }
          }}
          ticket={ticketDrawer.ticket}
        />
      )}

      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          mt: 1.5,
        }}
      >
        <SmallLoadingButton
          color="brandV2Yellow"
          disabled={!hasApprovableLoads || isApprovingAllLoads}
          loading={isApprovingAllLoads}
          onClick={handleApproveAllLoadsClick}
        >
          {t('approvals.files.approve_all_loads_cta')}
        </SmallLoadingButton>
      </Box>
    </Box>
  );
}
